import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ChakraProvider, Box, VStack, Heading, Text } from '@chakra-ui/react';
import theme from './theme.js';
import Header from './components/Header';
import PollsList from './components/PollsList';
import AudiencesList from './components/AudiencesList';
import PopulationsList from './components/PopulationsList';
import StagingPoll from './components/StagingPoll';
import PollsPage from './components/PollsPage';
import CreatePoll from './components/CreatePoll';
import PollCreationWizard from './components/PollCreationWizard';
import ManageQuestions from './components/ManageQuestions';
import PollCreator from './components/PollCreator';
import PollSettings from './components/PollSettings';
import { SurveyExperience } from './components/survey';
import PollResults from './components/PollResults';
import ResultsPage from './components/ResultsPage';
import LivePolls from './components/LivePolls';
import PreviewPoll from './components/PreviewPoll';
import ColorPalette from './components/colorbox.js';
import ErrorBoundary from './components/shared/ErrorBoundary';
import LivePollingDashboard from './components/LivePollingDashboard';
import SurveyMonitor from './components/SurveyMonitor';
import { AnalyticsDashboard } from './components/analytics';
import { AuthProvider } from './contexts/AuthContext';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import './utils/api'; // Initialize axios configuration

function PublicLanding() {
  useEffect(() => {
    document.title = 'Blackbird Research';
    return () => { document.title = 'Survey'; };
  }, []);

  return (
    <Box minH="100vh" bg="#FAFAF8" display="flex" alignItems="center" justifyContent="center">
      <VStack spacing={4} textAlign="center" px={6}>
        <Heading
          fontSize={{ base: '2xl', md: '3xl' }}
          fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          fontWeight="600"
          color="gray.800"
          letterSpacing="0.15em"
          textTransform="uppercase"
        >
          Blackbird Research
        </Heading>
        <Text fontSize="md" color="gray.500" maxW="400px">
          Public opinion research for the public good.
        </Text>
      </VStack>
    </Box>
  );
}

function App() {

  useEffect(() => {
    fetch('/api/csrf/', {
        credentials: 'include',
    })
    .then(response => response.json())
    .then(data => {
        console.log('CSRF cookie set:', data);
    })
    .catch(error => console.error('Error setting CSRF cookie:', error));
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <ErrorBoundary componentName="App">
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public landing page */}
              <Route path="/" element={<PublicLanding />} />
              {/* Public route - survey response (short URL) */}
              <Route path="/r/:token" element={
                <ErrorBoundary componentName="Survey">
                  <SurveyExperience />
                </ErrorBoundary>
              } />
              {/* Public route - survey response (legacy full URL) */}
              <Route path="/respond/:token" element={
                <ErrorBoundary componentName="Survey">
                  <SurveyExperience />
                </ErrorBoundary>
              } />
              <Route path="/preview-poll/:pollId" element={
                <ErrorBoundary componentName="Preview Poll">
                  <PreviewPoll />
                </ErrorBoundary>
              } />
              {/* Login route (obscured path) */}
              <Route path="/conduct0r" element={<Login />} />
              {/* Protected operator routes */}
              <Route path="/ops/*" element={
                <ProtectedRoute>
                  <MainApp />
                </ProtectedRoute>
              } />
              {/* Redirect old routes to /ops */}
              <Route path="*" element={
                <ProtectedRoute>
                  <MainApp />
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ChakraProvider>
  );
}

function MainApp() {
  return (
    <div className="App">
      <Header />
      <Routes>
        <Route index element={<Box minH="100vh" bg="background.shade2" />} />
        <Route path="colorbox" element={<ColorPalette />} />
        <Route path="pollsList" element={
          <ErrorBoundary componentName="Polls List">
            <PollsList />
          </ErrorBoundary>
        } />
        <Route path="audiences" element={
          <ErrorBoundary componentName="Audiences">
            <AudiencesList />
          </ErrorBoundary>
        } />
        <Route path="populations" element={
          <ErrorBoundary componentName="Populations">
            <PopulationsList />
          </ErrorBoundary>
        } />
        <Route path="stagingpoll" element={
          <ErrorBoundary componentName="Poll Staging">
            <StagingPoll />
          </ErrorBoundary>
        } />
        <Route path="pollsPage" element={
          <ErrorBoundary componentName="Polls Page">
            <PollsPage />
          </ErrorBoundary>
        } />
        <Route path="createPoll" element={
          <ErrorBoundary componentName="Create Poll">
            <CreatePoll />
          </ErrorBoundary>
        } />
        <Route path="create-poll" element={
          <ErrorBoundary componentName="Poll Creation Wizard">
            <PollCreationWizard />
          </ErrorBoundary>
        } />
        <Route path="manageQuestions" element={
          <ErrorBoundary componentName="Manage Questions">
            <ManageQuestions />
          </ErrorBoundary>
        } />
        <Route path="manage-questions/:pollId" element={
          <ErrorBoundary componentName="Manage Questions">
            <ManageQuestions />
          </ErrorBoundary>
        } />
        <Route path="polls/new" element={
          <ErrorBoundary componentName="New Poll">
            <PollCreator />
          </ErrorBoundary>
        } />
        <Route path="polls/:pollId/edit" element={
          <ErrorBoundary componentName="Edit Poll">
            <PollCreator />
          </ErrorBoundary>
        } />
        <Route path="polls/:pollId/settings" element={
          <ErrorBoundary componentName="Poll Settings">
            <PollSettings />
          </ErrorBoundary>
        } />
        <Route path="live-polls" element={
          <ErrorBoundary componentName="Live Polls">
            <LivePolls />
          </ErrorBoundary>
        } />
        <Route path="results" element={
          <ErrorBoundary componentName="Results">
            <ResultsPage />
          </ErrorBoundary>
        } />
        <Route path="polls/:pollId/results" element={
          <ErrorBoundary componentName="Poll Results">
            <PollResults />
          </ErrorBoundary>
        } />
        <Route path="live-poll/:pollId" element={
          <ErrorBoundary componentName="Live Polling Dashboard">
            <LivePollingDashboard />
          </ErrorBoundary>
        } />
        <Route path="live-poll/:pollId/monitor" element={
          <ErrorBoundary componentName="Survey Monitor">
            <SurveyMonitor />
          </ErrorBoundary>
        } />
        <Route path="analytics/:pollId" element={
          <ErrorBoundary componentName="Analytics Dashboard">
            <AnalyticsDashboard />
          </ErrorBoundary>
        } />
      </Routes>
    </div>
  );
}

export default App;
